import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Spin, message, Select, Typography, Button } from 'antd';
import {DownloadOutlined} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, RadarChart, Radar, PolarGrid, PolarAngleAxis, PieChart, Pie, Cell } from 'recharts';
import axiosInstance from '../../utils/axiosInstance';

const { Option } = Select;
const { Text } = Typography;

const COLORS = ['#465892', '#546DA6', '#BBC1C6', '#968EAF', '#6D5593', '#543D7B'];

const PlayerInsightsStats = ({ gameId }) => {
  const [playerStats, setPlayerStats] = useState([]);
  const [derivedStats, setDerivedStats] = useState([]);
  const [analyticalStats, setAnalyticalStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const [stdResponse, derivedResponse, anaResponse] = await Promise.all([
          axiosInstance.get(`/api/games/${gameId}/std_player_stats/`),
          axiosInstance.get(`/api/games/${gameId}/der_player_stats/`),
          axiosInstance.get(`/api/games/${gameId}/ana_player_stats/`)
        ]);
        setPlayerStats(stdResponse.data);
        setDerivedStats(derivedResponse.data);
        setAnalyticalStats(anaResponse.data);

        const uniqueTeams = [...new Set(stdResponse.data.map((stat) => stat.team))];
        setTeams(uniqueTeams);
      } catch (error) {
        message.error("Failed to fetch player stats.");
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [gameId]);

  useEffect(() => {
    const filteredPlayers = playerStats
      .filter((stat) => stat.team === selectedTeam)
      .map((stat) => stat.player_number);
    setPlayers([...new Set(filteredPlayers)]);
    setSelectedPlayer(null);
  }, [selectedTeam, playerStats]);

  const filteredStats = playerStats.filter((stat) => {
    return (!selectedTeam || stat.team === selectedTeam) && (!selectedPlayer || stat.player_number === selectedPlayer);
  });

  const filteredDerivedStats = derivedStats.filter((stat) => {
    return stat.team === selectedTeam && stat.player_number === selectedPlayer;
  });

  const filteredAnaStats = analyticalStats.filter((stat) => {
    return stat.team === selectedTeam && stat.player_number === selectedPlayer;
  });

  const percentageFormatter = (value) => `${(value * 100).toFixed(2)}%`;

  const handleExport = () => {
    const data = playerStats.map((stat) => {
      const derivedStat = derivedStats.find((d) => d.player_number === stat.player_number && d.team === stat.team) || {};
      const analyticalStat = analyticalStats.find((a) => a.player_number === stat.player_number && a.team === stat.team) || {};

      return {
        Player: stat.player_number,
        Team: stat.team,
        Points: derivedStat.pts || 0,
        Assists: stat.ast || 0,
        Rebounds: derivedStat.reb || 0,
        Turnovers: stat.tov || 0,
        FGPercentage: (derivedStat.fg_pct * 100).toFixed(2) + '%' || 'N/A',
        ThreePointPercentage: (derivedStat.three_pct * 100).toFixed(2) + '%' || 'N/A',
        FreeThrowPercentage: (derivedStat.ft_pct * 100).toFixed(2) + '%' || 'N/A',
        TrueShootingPercentage: (derivedStat.ts_pct * 100).toFixed(2) + '%' || 'N/A',
        EffectiveFGPercentage: (derivedStat.efg_pct * 100).toFixed(2) + '%' || 'N/A',
        UsageRate: (analyticalStat.usg_pct * 100).toFixed(2) + '%' || 'N/A',
        TurnoverPercentage: (analyticalStat.tov_pct * 100).toFixed(2) + '%' || 'N/A',
        OffensiveEfficiency: analyticalStat.oe?.toFixed(2) || 'N/A',
        EPS: derivedStat.eps?.toFixed(2) || 'N/A',
        EOP: derivedStat.eop?.toFixed(2) || 'N/A',
        Possessions: derivedStat.poss || 0,
        OREB: stat.oreb || 0,
        DREB: stat.dreb || 0,
        STL: stat.stl || 0,
        BLK: stat.blk || 0,
      };
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Player Stats");
    XLSX.writeFile(wb, `player_stats_game_${gameId}.xlsx`);
  };

  return (
    <div style={{ padding: '20px' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col span={12}>
          <Card
            title="Select Player"
            extra={
              <Button
                type="primary"
                onClick={handleExport}
                icon={<DownloadOutlined />}
                style={{ marginRight: 10 }}
              >
              </Button>
            }
          >
            <Select
              placeholder="Select Team"
              style={{ width: '100%', marginBottom: '10px' }}
              onChange={setSelectedTeam}
              allowClear
            >
              {teams.map((team) => (
                <Option key={team} value={team}>
                  {team}
                </Option>
              ))}
            </Select>
            <Select
              placeholder="Select Player"
              style={{ width: '100%' }}
              onChange={setSelectedPlayer}
              allowClear
              disabled={!selectedTeam}
            >
              {players.map((player) => (
                <Option key={player} value={player}>
                  Player {player}
                </Option>
              ))}
            </Select>
          </Card>
        </Col>
      </Row>

      {loading ? (
        // Display a centered loading spinner while loading
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Spin size="large" />
        </div>
      ) : selectedTeam && selectedPlayer ? (
        <>
          {/* Banner */}
            <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
            <Col span={6}>
                <Card title="Points" style={{ textAlign: 'center'}}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography.Text strong style={{ fontSize: '1.5rem' }}>{filteredDerivedStats[0]?.pts}</Typography.Text>
                </div>
                </Card>
            </Col>
            <Col span={6}>
                <Card title="Assists" style={{ textAlign: 'center'}}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography.Text strong style={{ fontSize: '1.5rem' }}>{filteredStats[0]?.ast}</Typography.Text>
                </div>
                </Card>
            </Col>
            <Col span={6}>
            <Card title="Rebounds" style={{ textAlign: 'center'}}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography.Text strong style={{ fontSize: '1.5rem' }}>{filteredDerivedStats[0]?.reb}</Typography.Text>
                </div>
                </Card>
            </Col>
            <Col span={6}>
                <Card title="Turnovers" style={{ textAlign: 'center'}}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography.Text strong style={{ fontSize: '1.5rem' }}>{filteredStats[0]?.tov}</Typography.Text>
                </div>
                </Card>
            </Col>
            </Row>


            {/* Shooting Distribution */}
            <Row gutter={[16, 16]} style={{ marginBottom: '20px', justifyContent: 'space-between' }}>
            <Col span={9}>
                <Card title="Shooting Percentages">
                <RadarChart outerRadius={90} width={300} height={250} data={[
                    { name: "FG%", value: filteredDerivedStats[0]?.fg_pct ?? 0, original: filteredDerivedStats[0]?.fg_pct },
                    { name: "3P%", value: filteredDerivedStats[0]?.three_pct ?? 0, original: filteredDerivedStats[0]?.three_pct },
                    { name: "FT%", value: filteredDerivedStats[0]?.ft_pct ?? 0, original: filteredDerivedStats[0]?.ft_pct },
                    { name: "TS%", value: filteredDerivedStats[0]?.ts_pct ?? 0, original: filteredDerivedStats[0]?.ts_pct },
                    { name: "eFG%", value: filteredDerivedStats[0]?.efg_pct ?? 0, original: filteredDerivedStats[0]?.efg_pct },
                ]}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="name" />
                    <Radar dataKey="value" stroke={COLORS[0]} fill={COLORS[0]} fillOpacity={0.6} />
                    <Tooltip formatter={(value, name, props) => (props.payload.original == null ? "N/A" : percentageFormatter(value))} />
                </RadarChart>
                </Card>
            </Col>
            <Col span={15}>
                <Card title="Shooting Distribution">
                <BarChart width={600} height={250} data={filteredStats}>
                    <XAxis dataKey="player_number" tick={false} />
                    <YAxis domain={[0, dataMax => Math.ceil(dataMax) + 2]} />
                    <Legend />  
                    <Bar dataKey="fga" fill={COLORS[0]} name="FGA" label={{ position: 'top' }} />
                    <Bar dataKey="fgm" fill={COLORS[1]} name="FGM" label={{ position: 'top' }} />
                    <Bar dataKey="three_pa" fill={COLORS[2]} name="3PA" label={{ position: 'top' }} />
                    <Bar dataKey="three_pm" fill={COLORS[3]} name="3PM" label={{ position: 'top' }} />
                    <Bar dataKey="fta" fill={COLORS[4]} name="FTA" label={{ position: 'top' }} />
                    <Bar dataKey="ftm" fill={COLORS[5]} name="FTM" label={{ position: 'top' }} />
                </BarChart>
                </Card>
            </Col>
            
            </Row>


            {/* Offense Efficiency */}
            <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>

            <Col span={7}>
                <Card title="Offensive Efficiency">
                <BarChart width={250} height={250} layout="vertical" data={[{ ...filteredDerivedStats[0], ...filteredAnaStats[0] }]}>
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="player_number" tick={false} />
                    <Tooltip formatter={(value, name) => (name === "OE" ? value.toFixed(2) : percentageFormatter(value))} />
                    <Legend />
                    <Bar dataKey="oe" fill={COLORS[2]} name="OE" />
                    <Bar dataKey="tov_pct" fill={COLORS[3]} name="TOV%" />
                    <Bar dataKey="usg_pct" fill={COLORS[5]} name="UR%" />
                </BarChart>
                </Card>
            </Col>

            {/* Efficient Offensive Production */}
            <Col span={10}>
            <Card title="Efficient Offensive Production">
                <BarChart width={400} height={250} data={filteredDerivedStats}>
                <XAxis dataKey="player_number" tick={false} />
                <YAxis domain={[0, dataMax => Math.ceil(dataMax) + 2]} />
                <Legend />
                <Bar dataKey="eps" fill={COLORS[1]} name="EPS" label={{ position: 'top', formatter: (value) => value.toFixed(2) }} />
                <Bar dataKey="eop" fill={COLORS[4]} name="EOP" label={{ position: 'top', formatter: (value) => value.toFixed(2) }} />
                <Bar dataKey="poss" fill={COLORS[5]} name="POSS" label={{ position: 'top', formatter: (value) => value.toFixed(2) }} />
                </BarChart>
            </Card>
            </Col>

            {/* Radar Chart with pts_poss, eps_poss, and eop_poss */}
            <Col span={7}>
                <Card title="Production per Possession">
                <RadarChart outerRadius={80} width={300} height={250} data={[
                    { name: "PTS/POSS", value: filteredDerivedStats[0]?.pts_poss },
                    { name: "EPS/POSS", value: filteredDerivedStats[0]?.eps_poss },
                    { name: "EOP/POSS", value: filteredDerivedStats[0]?.eop_poss },
                ]}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="name" />
                    <Radar dataKey="value" stroke={COLORS[0]} fill={COLORS[0]} fillOpacity={0.6} />
                    <Tooltip formatter={(value) => value?.toFixed(2) || "N/A"} />
                </RadarChart>
                </Card>
            </Col>
            </Row>
 
            {/* Playmaking & Efficiency */}
            <Row gutter={[16, 16]}>
            {/* Doughnut Chart with AST, TOV, and AST/TO Ratio */}
            <Col span={8}>
                <Card title="Playmaking Abilities">
                <PieChart width={250} height={250}>
                    <Pie
                    data={[
                        { name: 'AST', value: filteredStats[0]?.ast },
                        { name: 'TOV', value: filteredStats[0]?.tov },
                    ]}
                    dataKey="value"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    label={false} // Disable labels on the chart
                    >
                    <Cell fill={COLORS[0]} />
                    <Cell fill={COLORS[2]} />
                    </Pie>
                    {/* Display AST/TO ratio in the center of the doughnut */}
                    <Tooltip formatter={(value) => value || "N/A"} />
                    <text x="50%" y="40%" textAnchor="middle" dominantBaseline="middle" fontSize="14">
                    AST/TO
                    </text>
                    <text x="50%" y="55%" textAnchor="middle" dominantBaseline="middle" fontSize="20">
                    {filteredDerivedStats[0]?.ast_to ? filteredDerivedStats[0]?.ast_to.toFixed(2) : "N/A"}
                    </text>
                    <Legend layout="horizontal" verticalAlign="bottom" align="center" />
                </PieChart>
                </Card>
            </Col>

            <Col span={8}>
            <Card title="Rebounds">
                <PieChart width={300} height={250}>
                <Pie
                    data={[
                    { name: 'OREB', value: filteredStats[0]?.oreb, pct: filteredAnaStats[0]?.oreb_pct },
                    { name: 'DREB', value: filteredStats[0]?.dreb, pct: filteredAnaStats[0]?.dreb_pct },
                    ]}
                    dataKey="value"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ value }) => value}
                >
                    <Cell fill={COLORS[0]} />
                    <Cell fill={COLORS[2]} />
                </Pie>
                <Tooltip 
                    formatter={(value, name, props) => [
                    `${props.payload.pct ? percentageFormatter(props.payload.pct) : "N/A"}`,
                    `${name}%`
                    ]} 
                />
                <Legend layout="horizontal" verticalAlign="bottom" align="center" />
                </PieChart>
            </Card>
            </Col>

              <Col span={8}>
                <Card title="Defensive Impact">
                  <BarChart width={300} height={250} data={filteredStats}>
                    <XAxis dataKey="player_number" tick={false} />
                    <YAxis domain={[0, dataMax => Math.ceil(dataMax) + 2]} />
                    <Legend />
                    <Bar dataKey="stl" fill={COLORS[1]} name="STL" label={{ position: 'top' }}/>
                    <Bar dataKey="blk" fill={COLORS[3]} name="BLK" label={{ position: 'top' }}/>
                  </BarChart>
                </Card>
              </Col>
            </Row>
          </>
        ) : null}
      </div>
    );
  };

export default PlayerInsightsStats;

           
